import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Typography, Box, Chip, CircularProgress } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlagIcon from "@mui/icons-material/Flag";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { api } from "common";
import { WEB_MAIN_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { colors } from "../components/Theme/WebTheme";
import ConfirmationDialogRaw from "../components/ConfirmationDialogRaw";
import AlertDialog from "../components/AlertDialog";

const BookingQueue = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cancelBooking, updateBooking } = api;

  const settings = useSelector((state) => state.settingsdata.settings);
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const role = useSelector((state) => state.auth.profile.usertype);

  const MAIN_COLOR = WEB_MAIN_COLOR(settings?.appCat);

  const [queuedBookings, setQueuedBookings] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const [acceptingId, setAcceptingId] = useState(null);

  useEffect(() => {
    if (bookinglistdata.bookings) {
      const newBookings = bookinglistdata.bookings.filter(
        (b) => b.status === "NEW"
      );
      setQueuedBookings(newBookings);
    } else {
      setQueuedBookings([]);
    }
  }, [bookinglistdata.bookings]);

  const formatAmount = (value) => {
    const number = parseFloat(value || 0);
    if (settings?.country === "Vietnam") {
      return number.toLocaleString("vi-VN", {
        minimumFractionDigits: settings?.decimal || 0,
        maximumFractionDigits: settings?.decimal || 0,
      });
    }
    return number.toLocaleString("en-US", {
      minimumFractionDigits: settings?.decimal || 2,
      maximumFractionDigits: settings?.decimal || 2,
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const d = new Date(timestamp);
    return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleAccept = (booking) => {
    if (!settings.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: t("demo_mode") });
      return;
    }
    setAcceptingId(booking.id);
    const updated = { ...booking, status: "ACCEPTED" };
    dispatch(updateBooking(updated));
    setTimeout(() => setAcceptingId(null), 1500);
  };

  const handleCancelClick = (booking) => {
    if (!settings.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: t("demo_mode") });
      return;
    }
    setSelectedBooking(booking);
    setOpenConfirm(true);
  };

  const onConfirmClose = (value) => {
    if (value && selectedBooking) {
      dispatch(cancelBooking({ reason: value, booking: selectedBooking, cancelledBy: role }));
    }
    setOpenConfirm(false);
    setSelectedBooking(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "NEW": return "#f59e0b";
      case "ACCEPTED": return colors.GREEN;
      case "CANCELLED": return colors.RED;
      default: return colors.CARD_LABEL;
    }
  };

  if (bookinglistdata.loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress style={{ color: MAIN_COLOR }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, fontFamily: FONT_FAMILY }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${MAIN_COLOR} 0%, ${MAIN_COLOR}dd 100%)`,
          borderRadius: "16px",
          p: 3,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: `0 4px 20px ${MAIN_COLOR}44`,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, fontFamily: FONT_FAMILY }}>
            {t("booking_title") || "Booking Queue"}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", fontFamily: FONT_FAMILY, mt: 0.5 }}>
            {queuedBookings.length} {queuedBookings.length === 1 ? "incoming booking" : "incoming bookings"}
          </Typography>
        </Box>
        <Box
          sx={{
            background: "rgba(255,255,255,0.2)",
            borderRadius: "12px",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <DirectionsCarIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Typography sx={{ color: "#fff", fontWeight: 600, fontFamily: FONT_FAMILY }}>
            {queuedBookings.length}
          </Typography>
        </Box>
      </Box>

      {/* Empty State */}
      {queuedBookings.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 10,
            background: "#f8fafc",
            borderRadius: "16px",
            border: "2px dashed #e2e8f0",
          }}
        >
          <DirectionsCarIcon sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "#94a3b8", fontFamily: FONT_FAMILY }}>
            No incoming bookings
          </Typography>
          <Typography variant="body2" sx={{ color: "#cbd5e1", fontFamily: FONT_FAMILY, mt: 1 }}>
            New bookings will appear here
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
            gap: 2,
          }}
        >
          {queuedBookings.map((booking) => (
            <BookingCard
              key={booking.id}
              booking={booking}
              settings={settings}
              MAIN_COLOR={MAIN_COLOR}
              formatAmount={formatAmount}
              formatDate={formatDate}
              getStatusColor={getStatusColor}
              onAccept={handleAccept}
              onCancel={handleCancelClick}
              isAccepting={acceptingId === booking.id}
              t={t}
              role={role}
            />
          ))}
        </Box>
      )}

      <ConfirmationDialogRaw open={openConfirm} onClose={onConfirmClose} value="" />
      <AlertDialog open={commonAlert.open} onClose={() => setCommonAlert({ open: false, msg: "" })}>
        {commonAlert.msg}
      </AlertDialog>
    </Box>
  );
};

const BookingCard = ({
  booking, settings, MAIN_COLOR, formatAmount, formatDate,
  getStatusColor, onAccept, onCancel, isAccepting, t, role,
}) => {
  const symbol = settings?.symbol || "$";
  const swipeSymbol = settings?.swipe_symbol !== false;
  const fare = booking.trip_cost || booking.estimateFare || 0;
  const fareDisplay = swipeSymbol
    ? `${formatAmount(fare)} ${symbol}`
    : `${symbol} ${formatAmount(fare)}`;

  const canAccept = role === "admin" || role === "fleetadmin";
  const canCancel = role === "admin" || role === "fleetadmin" || role === "customer";

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
        border: "1px solid #f1f5f9",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${MAIN_COLOR} 0%, ${MAIN_COLOR}cc 100%)`,
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <DirectionsCarIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14, fontFamily: FONT_FAMILY }}>
            #{booking.id?.slice(-6)?.toUpperCase() || "------"}
          </Typography>
        </Box>
        <Chip
          label={booking.status}
          size="small"
          sx={{
            backgroundColor: getStatusColor(booking.status),
            color: "#fff",
            fontWeight: 600,
            fontSize: 11,
            fontFamily: FONT_FAMILY,
            height: 24,
          }}
        />
      </Box>

      {/* Card Body */}
      <Box sx={{ px: 2.5, py: 2, flex: 1 }}>
        {/* Customer Info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: `${MAIN_COLOR}18`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PersonIcon sx={{ color: MAIN_COLOR, fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, fontFamily: FONT_FAMILY, color: "#1e293b" }}>
              {booking.customer_name || `${booking.customer_firstName || ""} ${booking.customer_lastName || ""}`.trim() || "Customer"}
            </Typography>
            {booking.customer_contact && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <PhoneIcon sx={{ fontSize: 11, color: "#94a3b8" }} />
                <Typography sx={{ fontSize: 11, color: "#94a3b8", fontFamily: FONT_FAMILY }}>
                  {booking.customer_contact}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ height: "1px", background: "#f1f5f9", mb: 1.5 }} />

        {/* Pickup */}
        <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
          <LocationOnIcon sx={{ color: MAIN_COLOR, fontSize: 18, mt: 0.2, flexShrink: 0 }} />
          <Box>
            <Typography sx={{ fontSize: 10, color: "#94a3b8", fontFamily: FONT_FAMILY, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Pickup
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#334155", fontFamily: FONT_FAMILY, lineHeight: 1.3 }}>
              {booking.pickupAddress || "—"}
            </Typography>
          </Box>
        </Box>

        {/* Drop */}
        <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
          <FlagIcon sx={{ color: "#ef4444", fontSize: 18, mt: 0.2, flexShrink: 0 }} />
          <Box>
            <Typography sx={{ fontSize: 10, color: "#94a3b8", fontFamily: FONT_FAMILY, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Drop-off
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#334155", fontFamily: FONT_FAMILY, lineHeight: 1.3 }}>
              {booking.dropAddress || "—"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ height: "1px", background: "#f1f5f9", mb: 1.5 }} />

        {/* Meta row */}
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <AccessTimeIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
            <Typography sx={{ fontSize: 12, color: "#64748b", fontFamily: FONT_FAMILY }}>
              {formatDate(booking.tripdate || booking.bookingDate)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <AttachMoneyIcon sx={{ fontSize: 14, color: MAIN_COLOR }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: MAIN_COLOR, fontFamily: FONT_FAMILY }}>
              {fareDisplay}
            </Typography>
          </Box>
        </Box>

        {/* Car type & payment */}
        <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
          {booking.carType && (
            <Chip
              icon={<DirectionsCarIcon style={{ fontSize: 13 }} />}
              label={booking.carType}
              size="small"
              sx={{ fontSize: 11, height: 22, fontFamily: FONT_FAMILY, background: `${MAIN_COLOR}12`, color: MAIN_COLOR }}
            />
          )}
          {booking.payment_mode && (
            <Chip
              label={booking.payment_mode}
              size="small"
              sx={{ fontSize: 11, height: 22, fontFamily: FONT_FAMILY, background: "#f1f5f9", color: "#64748b", textTransform: "capitalize" }}
            />
          )}
          {booking.bookLater && (
            <Chip
              label="Scheduled"
              size="small"
              sx={{ fontSize: 11, height: 22, fontFamily: FONT_FAMILY, background: "#fef3c7", color: "#d97706" }}
            />
          )}
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ px: 2.5, pb: 2.5, pt: 1, display: "flex", gap: 1.5 }}>
        {canCancel && (
          <Box
            onClick={() => onCancel(booking)}
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: "12px",
              border: `2px solid #fee2e2`,
              background: "#fff5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.8,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { background: "#fee2e2", borderColor: colors.RED },
            }}
          >
            <CancelIcon sx={{ color: colors.RED, fontSize: 18 }} />
            <Typography sx={{ color: colors.RED, fontWeight: 600, fontSize: 13, fontFamily: FONT_FAMILY }}>
              {t("cancel")}
            </Typography>
          </Box>
        )}
        {canAccept && (
          <Box
            onClick={() => !isAccepting && onAccept(booking)}
            sx={{
              flex: 2,
              py: 1.5,
              borderRadius: "12px",
              background: isAccepting
                ? `${MAIN_COLOR}88`
                : `linear-gradient(135deg, ${MAIN_COLOR} 0%, ${MAIN_COLOR}dd 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.8,
              cursor: isAccepting ? "default" : "pointer",
              boxShadow: `0 4px 12px ${MAIN_COLOR}44`,
              transition: "all 0.2s",
              "&:hover": !isAccepting ? { transform: "scale(1.02)", boxShadow: `0 6px 20px ${MAIN_COLOR}66` } : {},
            }}
          >
            {isAccepting ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              <>
                <CheckCircleIcon sx={{ color: "#fff", fontSize: 18 }} />
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 13, fontFamily: FONT_FAMILY }}>
                  Accept Booking
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BookingQueue;
